<template>
  <v-container fluid class="pa-0">
    <!-- Hero Header -->
    <v-sheet color="primary" dark class="hero-header pa-6 pa-md-10">
      <v-row align="center" justify="center">
        <v-col cols="12" md="8" class="text-center">
          <v-avatar size="64" color="white" class="mb-4 elevation-4">
            <v-icon size="36" color="primary">mdi-account-multiple-plus</v-icon>
          </v-avatar>
          <h1 class="text-h4 text-md-h3 font-weight-bold white--text mb-2">
            Add Prospects
          </h1>
          <p class="text-subtitle-1 white--text text--lighten-3 mb-0">
            Upload a file or manually enter your contacts
          </p>
        </v-col>
      </v-row>
    </v-sheet>

    <v-container class="py-8">
      <v-row justify="center">
        <v-col cols="12" xl="10">

                      <!-- Default List Info -->
            <!-- Default List Info -->
            <v-card
              v-if="defaultList"
              class="mb-6 list-info-card"
              elevation="2"
              outlined
              @click="goToTargetList"
              style="cursor: pointer;"
            >
              <v-card-text class="pa-4 d-flex align-center">
                <v-avatar size="40" color="primary lighten-5" class="mr-3">
                  <v-icon color="primary">mdi-playlist-check</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption grey--text">Target List</div>
                  <div class="font-weight-bold text-body-1">{{ defaultList.name }}</div>
                </div>
                <v-chip x-small color="primary" dark class="ml-auto">Default</v-chip>
              </v-card-text>
            </v-card>
          <!-- Main Card -->
          <v-card class="main-card" elevation="3">
            <!-- Tabs -->
            <v-tabs
              v-model="tab"
              background-color="primary"
              dark
              grow
              slider-color="white"
              class="rounded-t-lg"
            >
              <v-tab class="py-3">
                <v-icon left>mdi-cloud-upload</v-icon>
                <span class="hidden-xs-only">Upload File</span>
                <span class="hidden-sm-and-up">Upload</span>
              </v-tab>
              <v-tab class="py-3">
                <v-icon left>mdi-pencil</v-icon>
                <span class="hidden-xs-only">Manual Entry</span>
                <span class="hidden-sm-and-up">Manual</span>
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab" class="pa-4 pa-md-6">
              <!-- Upload Tab -->
              <v-tab-item>
                <!-- Drag & Drop Zone -->
                <v-card
                  outlined
                  class="upload-zone pa-6 pa-md-8 text-center mb-4"
                  :class="{ 'upload-zone--dragover': isDragging, 'upload-zone--has-file': uploadFile }"
                  @dragover.prevent="isDragging = true"
                  @dragleave.prevent="isDragging = false"
                  @drop.prevent="handleDrop"
                  @click="!uploadFile && $refs.fileInput.click()"
                >
                  <input
                    ref="fileInput"
                    type="file"
                    accept=".xlsx,.xls,.pdf,.jpg,.jpeg,.png,.txt,.csv"
                    class="d-none"
                    @change="handleFileChange"
                  >

                  <v-expand-transition>
                    <div v-if="!uploadFile">
                      <v-icon size="56" color="primary" class="mb-3 upload-icon">mdi-cloud-upload</v-icon>
                      <div class="text-h6 font-weight-medium mb-2">Drop your file here</div>
                      <div class="text-body-2 grey--text mb-4">
                        or click to browse from your device
                      </div>
                      <v-btn color="primary" outlined rounded large>
                        <v-icon left>mdi-folder-open</v-icon>
                        Choose File
                      </v-btn>
                    </div>
                  </v-expand-transition>

                  <!-- File Selected -->
                  <v-expand-transition>
                    <div v-if="uploadFile" class="text-left">
                      <div class="d-flex align-center">
                        <v-avatar size="48" :color="isImageFile ? 'success' : 'primary'" class="mr-4">
                          <v-icon color="white" size="28">{{ isImageFile ? 'mdi-image' : 'mdi-file-document' }}</v-icon>
                        </v-avatar>
                        <div class="flex-grow-1">
                          <div class="font-weight-bold text-body-1">{{ uploadFile.name }}</div>
                          <div class="text-caption grey--text">{{ formatFileSize(uploadFile.size) }}</div>
                        </div>
                        <v-btn icon color="error" @click.stop="clearFile">
                          <v-icon>mdi-close-circle</v-icon>
                        </v-btn>
                      </div>

                      <!-- Image Preview -->
                      <v-expand-transition>
                        <div v-if="isImageFile && imagePreview" class="mt-4">
                          <v-img
                            :src="imagePreview"
                            max-height="250"
                            contain
                            class="rounded-lg elevation-2"
                          ></v-img>
                        </div>
                      </v-expand-transition>

                      <v-btn
                        color="primary"
                        class="mt-4"
                        :loading="processing"
                        @click.stop="processFile"
                      >
                        <v-icon left>mdi-magic-staff</v-icon>
                        Extract Contacts
                      </v-btn>
                    </div>
                  </v-expand-transition>
                </v-card>

                <!-- Image Tip -->
                <v-alert
                  dense
                  text
                  color="info"
                  class="mb-4"
                  icon="mdi-lightning-bolt"
                >
                  <div class="font-weight-medium text-body-2">Tip: Images are faster to analyze!</div>
                  <div class="text-caption grey--text mt-1">
                    Take a photo of your contact list, business cards, or a spreadsheet. Our AI extracts names, phone numbers, and emails automatically. Much faster than manual Excel entry.
                  </div>
                </v-alert>

                <!-- Processing -->
                <v-expand-transition>
                  <div v-if="processing" class="mb-4">
                    <v-card outlined class="pa-4 processing-card">
                      <div class="d-flex align-center">
                        <v-progress-circular indeterminate color="primary" size="32" width="3" class="mr-3"></v-progress-circular>
                        <div>
                          <div class="font-weight-medium">Analyzing your file...</div>
                          <div class="text-caption grey--text">This may take a few seconds</div>
                        </div>
                      </div>
                    </v-card>
                  </div>
                </v-expand-transition>

                <!-- Extracted Results -->
                <v-expand-transition>
                  <div v-if="extractedProspects.length">
                    <div class="d-flex align-center mb-3">
                      <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                      <span class="font-weight-bold text-body-1">{{ extractedProspects.length }} contact(s) extracted</span>
                      <v-spacer></v-spacer>
                      <v-btn text small color="error" @click="extractedProspects = []">
                        <v-icon left x-small>mdi-delete-sweep</v-icon>Clear All
                      </v-btn>
                    </div>
                    <v-data-table
                      :headers="headers"
                      :items="extractedProspects"
                      dense
                      class="elevation-1 prospects-table"
                      hide-default-footer
                      disable-pagination
                    >
                      <template v-slot:item.actions="{ index }">
                        <v-btn icon x-small color="error" @click="removeExtracted(index)">
                          <v-icon x-small>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                    </v-data-table>
                  </div>
                </v-expand-transition>
              </v-tab-item>

              <!-- Manual Entry Tab -->
              <v-tab-item>
                <v-card outlined class="pa-4 pa-md-6 mb-4 manual-entry-card">
                  <div class="text-subtitle-1 font-weight-bold mb-4">
                    <v-icon color="primary" class="mr-2">mdi-account-plus</v-icon>
                    Add Contact Manually
                  </div>
                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        v-model="manual.prospect_name"
                        label="Full Name *"
                        dense
                        outlined
                        prepend-inner-icon="mdi-account"
                        :rules="[v => !!v || 'Name is required']"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2">
                      <v-text-field
                        v-model="manual.phone_number"
                        label="Phone"
                        dense
                        outlined
                        prepend-inner-icon="mdi-phone"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        v-model="manual.email"
                        label="Email"
                        dense
                        outlined
                        prepend-inner-icon="mdi-email"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="4" md="2">
                      <v-text-field
                        v-model="manual.profession"
                        label="Profession"
                        dense
                        outlined
                        prepend-inner-icon="mdi-briefcase"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="4" md="1">
                      <v-text-field
                        v-model="manual.location"
                        label="City"
                        dense
                        outlined
                        prepend-inner-icon="mdi-map-marker"
                      ></v-text-field>
                    </v-col>
                      <v-col cols="12" sm="6" md="2">
                        <v-text-field
                          v-model="manual.preferences"
                          label="Interests / Prefs"
                          dense
                          outlined
                          prepend-inner-icon="mdi-star-outline"
                          placeholder="Ex: Épargne, immobilier…"
                        ></v-text-field>
                      </v-col>
                    <v-col cols="12" sm="4" md="1" class="d-flex align-center justify-center">
                      <v-btn
                        fab
                        small
                        color="primary"
                        :disabled="!manual.prospect_name"
                        @click="addManualProspect"
                        class="add-btn"
                      >
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card>

                <!-- Manual Results -->
                <v-expand-transition>
                  <div v-if="manualProspects.length">
                    <div class="d-flex align-center mb-3">
                      <v-icon color="info" class="mr-2">mdi-account-check</v-icon>
                      <span class="font-weight-bold text-body-1">{{ manualProspects.length }} contact(s) added manually</span>
                      <v-spacer></v-spacer>
                      <v-btn text small color="error" @click="manualProspects = []">
                        <v-icon left x-small>mdi-delete-sweep</v-icon>Clear All
                      </v-btn>
                    </div>
                    <v-data-table
                      :headers="headers"
                      :items="manualProspects"
                      dense
                      class="elevation-1 prospects-table"
                      hide-default-footer
                      disable-pagination
                    >
                      <template v-slot:item.actions="{ index }">
                        <v-btn icon x-small color="error" @click="removeManual(index)">
                          <v-icon x-small>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                    </v-data-table>
                  </div>
                </v-expand-transition>
              </v-tab-item>
            </v-tabs-items>

            <v-divider class="mx-4 mx-md-6"></v-divider>

            <!-- Footer Summary -->
            <v-card-text class="pa-4 pa-md-6">
              <v-row align="center">
                <v-col cols="12" sm="6">
                  <v-card outlined class="summary-chip pa-3 d-flex align-center">
                    <v-avatar size="40" color="primary lighten-5" class="mr-3">
                      <v-icon color="primary">mdi-account-group</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-caption grey--text">Total Contacts Ready</div>
                      <div class="text-h5 font-weight-bold primary--text">{{ totalProspects }}</div>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" class="text-sm-right mt-3 mt-sm-0">
                  <v-btn
                    color="primary"
                    x-large
                    :disabled="!canSave"
                    :loading="saving"
                    @click="saveProspects"
                    class="save-btn"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    Save {{ totalProspects }} Contact(s)
                  </v-btn>
                  <div v-if="!defaultListId" class="text-caption error--text mt-2">
                    <v-icon x-small color="error">mdi-alert</v-icon>
                    Loading default list...
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      top
      right
    >
      <div class="d-flex align-center">
        <v-icon left>{{ snackbar.icon }}</v-icon>
        {{ snackbar.message }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AddProspects',

  data() {
    return {
      tab: 0,
      isDragging: false,
      uploadFile: null,
      imagePreview: null,
      processing: false,
      extractedProspects: [],
      manual: {
      prospect_name: '', phone_number: '', email: '',
      profession: '', location: '', preferences: ''   // ← renommé
    },
      manualProspects: [],
      saving: false,
      defaultListId: null,
      defaultListName: '',
      snackbar: { show: false, message: '', color: 'success', icon: 'mdi-check-circle' },
    }
  },

  computed: {
    headers() {
      return [
        { text: 'Name',       value: 'prospect_name' },
        { text: 'Phone',      value: 'phone_number' },
        { text: 'Email',      value: 'email' },
        { text: 'Profession', value: 'profession' },
        { text: 'Location',   value: 'location' },
        { text: 'Interests / Prefs', value: 'preferences' },   // ← renommé   // ← nouveau
        { text: '',           value: 'actions', sortable: false, width: '50px', align: 'center' },
      ]
    },
    totalProspects() {
      return this.extractedProspects.length + this.manualProspects.length
    },
    canSave() {
      return this.totalProspects > 0 && !!this.defaultListId
    },
    defaultList() {
      return this.defaultListId ? { id: this.defaultListId, name: this.defaultListName } : null
    },
    isImageFile() {
      if (!this.uploadFile) return false
      return this.uploadFile.type.startsWith('image/')
    },
  },

  watch: {
    uploadFile(newVal) {
      if (newVal && this.isImageFile) {
        this.generateImagePreview(newVal)
      } else {
        this.imagePreview = null
      }
    },
  },

  async mounted() {
    await this.loadDefaultList()
    document.title = 'TopUp | Add Prospects'
  },

  methods: {
    async loadDefaultList() {
      try {
        const res = await axios.get('/api/v1/topup/default-prospect-list/')
        this.defaultListId = res.data.id
        this.defaultListName = res.data.name
      } catch (e) {
        this.showToast('Failed to load default prospect list', 'error')
      }
    },

    handleFileChange(e) {
      const file = e.target.files[0]
      if (file) this.uploadFile = file
    },

    handleDrop(e) {
      this.isDragging = false
      const file = e.dataTransfer.files[0]
      if (file) this.uploadFile = file
    },

    generateImagePreview(file) {
      const reader = new FileReader()
      reader.onload = (e) => { this.imagePreview = e.target.result }
      reader.readAsDataURL(file)
    },

    clearFile() {
      this.uploadFile = null
      this.imagePreview = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    async processFile() {
      if (!this.uploadFile) return
      this.processing = true
      const fd = new FormData()
      fd.append('file', this.uploadFile)
      try {
        const res = await axios.post('/api/v1/topup/process-prospect-file/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.extractedProspects = res.data.prospects || []
        this.showToast('Extracted ' + this.extractedProspects.length + ' prospects', 'success')
      } catch (err) {
        let msg = 'Extraction failed.'
        if (err.response && err.response.data && err.response.data.error) msg = err.response.data.error
        this.showToast(msg, 'error')
      } finally {
        this.processing = false
      }
    },
    goToTargetList() {
  if (this.defaultListId) {
    this.$router.push(`/savings/marketing/view/prospects/${this.defaultListId}`);
  }
},

    addManualProspect() {
      const m = this.manual
      if (!m.prospect_name) return
      this.manualProspects.push({ ...m })
      for (let k in this.manual) this.manual[k] = ''
      this.showToast('Contact added', 'success')
    },

    removeManual(index) { this.manualProspects.splice(index, 1) },
    removeExtracted(index) { this.extractedProspects.splice(index, 1) },

    async saveProspects() {
      if (!this.canSave) return
      this.saving = true
      const allProspects = [...this.extractedProspects, ...this.manualProspects]
      try {
        const res = await axios.post('/api/v1/topup/add/bulk_prospects/', {
          list_id: this.defaultListId,
          prospects: allProspects,
        })
        this.showToast(res.data.created + ' prospects saved successfully', 'success')
        setTimeout(() => {
          this.$router.push('/savings/marketing/view/prospects/' + this.defaultListId)
        }, 1500)
      } catch (err) {
        let msg = 'Failed to save prospects'
        if (err.response && err.response.data && err.response.data.error) msg = err.response.data.error
        this.showToast(msg, 'error')
      } finally {
        this.saving = false
      }
    },

    showToast(msg, type) {
      this.snackbar = {
        show: true,
        message: msg,
        color: type === 'success' ? 'success' : 'error',
        icon: type === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle',
      }
    },
  },
}
</script>

<style scoped>
.hero-header {
  border-radius: 0 0 24px 24px;
  position: relative;
  overflow: hidden;
}
.hero-header::after {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 60%;
  height: 200%;
  background: rgba(255,255,255,0.05);
  border-radius: 50%;
}

.list-info-card {
  border-radius: 12px;
}

.main-card {
  border-radius: 16px;
  overflow: hidden;
}

.upload-zone {
  border: 2px dashed rgba(0,0,0,0.15);
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  background: #fafafa;
  position: relative;
}
.upload-zone:hover {
  border-color: var(--v-primary-base);
  background: var(--v-primary-lighten5);
}
.upload-zone--dragover {
  border-color: var(--v-primary-base);
  background: var(--v-primary-lighten4);
  transform: scale(1.01);
}
.upload-zone--has-file {
  border-style: solid;
  border-color: var(--v-primary-base);
  background: white;
  cursor: default;
}
.upload-icon {
  transition: transform 0.3s ease;
}
.upload-zone:hover .upload-icon {
  transform: translateY(-4px);
}

.processing-card {
  border-radius: 12px;
  background: var(--v-primary-lighten5);
}

.manual-entry-card {
  border-radius: 12px;
  background: #fafafa;
}

.add-btn {
  transition: transform 0.2s;
}
.add-btn:hover {
  transform: scale(1.1);
}

.prospects-table ::v-deep th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0,0,0,0.6) !important;
  font-weight: 600 !important;
  background: #f8f9fa !important;
}

.summary-chip {
  border-radius: 12px;
  display: inline-flex;
}

.save-btn {
  border-radius: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: none;
  font-size: 1.05rem;
  height: 48px;
}

@media (max-width: 600px) {
  .hero-header {
    border-radius: 0 0 16px 16px;
    padding: 24px 16px !important;
  }
  .upload-zone {
    padding: 24px 16px !important;
  }
  .save-btn {
    width: 100%;
  }
}
</style>
